import React, { useState, useEffect } from 'react';
import {
  Fab,
  Badge
} from '@mui/material';
import {
  SmartToy as AIIcon
} from '@mui/icons-material';
import geminiService from './services/geminiService';
import ChatDialog from './components/ChatDialog';
import QuickActions from './components/QuickActions';

const AIAssistant = ({ userRole = 'recruiter', currentPage, candidateCount = 0 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [serviceStatus, setServiceStatus] = useState('online'); // 'online', 'busy', 'offline'
  const [loadingTime, setLoadingTime] = useState(0);

  useEffect(() => {
    // Initialize with welcome message
    if (messages.length === 0) {
      const userLanguage = localStorage.getItem("userLanguage") || 'fr';
      const isFrench = userLanguage === 'fr';
      
      const welcomeMessage = isFrench ? 
        "👋 Salut ! Je suis Talenty Hire, ton assistant IA intelligent pour notre plateforme ATS complète ! 😊\n\n🎯 Je suis là pour t'aider à maximiser l'efficacité de Talenty Hire :\n• 📝 Créer des offres d'emploi optimisées\n• 👥 Évaluer et classer automatiquement les candidats\n• ❓ Créer des questions d'entretien personnalisées\n• 📄 Analyser intelligemment les CV\n• 📧 Utiliser notre éditeur d'emails intégré\n• 🏆 Optimiser tes processus de recrutement\n• 🎯 Scraper et organiser automatiquement les candidatures\n• 📊 Importer et analyser ta base de données CV\n• 🇨🇦 Créer des CV canadiens optimisés\n\n💡 Tout est intégré dans Talenty Hire - pas besoin d'aller ailleurs !\n\nAvec quoi puis-je t'aider aujourd'hui ? 🚀" :
        "👋 Hi there! I'm Talenty Hire, your intelligent AI assistant for our comprehensive ATS platform! 😊\n\n🎯 I'm here to help you maximize Talenty Hire's efficiency:\n• 📝 Create optimized job postings\n• 👥 Evaluate and automatically rank candidates\n• ❓ Create personalized interview questions\n• 📄 Intelligently analyze resumes\n• 📧 Use our integrated email editor\n• 🏆 Optimize your recruitment processes\n• 🎯 Automatically scrape and organize applications\n• 📊 Import and analyze your CV database\n• 🇨🇦 Create optimized Canadian resumes\n\n💡 Everything is integrated in Talenty Hire - no need to go elsewhere!\n\nWhat would you like help with today? 🚀";
      
      setMessages([{
        id: 1,
        text: welcomeMessage,
        type: 'ai',
        timestamp: new Date()
      }]);
    }
  }, []);

  // Update unread count when chat is closed and new messages arrive
  useEffect(() => {
    if (!isOpen && messages.length > 1) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.type === 'ai') {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [messages, isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      type: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setLoadingTime(0);
    
    // Start loading timer
    const loadingInterval = setInterval(() => {
      setLoadingTime(prev => prev + 1);
    }, 1000);
    
    // Scroll to bottom immediately after user message
    setTimeout(() => {
      const messagesContainer = document.querySelector('[data-messages-container]');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);

    try {
      const context = {
        userRole,
        currentPage,
        candidateCount
      };

      const response = await geminiService.generateResponse(inputValue, context);
      
      const aiMessage = {
        id: Date.now() + 1,
        text: response,
        type: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      setServiceStatus('online'); // Reset status on successful response
    } catch (error) {
      // Update service status based on error
      if (error.message.includes('busy') || error.message.includes('overloaded')) {
        setServiceStatus('busy');
      } else if (error.message.includes('network') || error.message.includes('connection')) {
        setServiceStatus('offline');
      }
      
      // Try to provide a helpful fallback response
      const fallbackResponse = geminiService.getFallbackResponse(inputValue);
      const fallbackMessage = {
        id: Date.now() + 1,
        text: fallbackResponse,
        type: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      clearInterval(loadingInterval);
      setIsLoading(false);
      setLoadingTime(0);
    }
  };

  const handleQuickAction = async (action) => {
    setIsLoading(true);
    
    // Scroll to bottom immediately when quick action is triggered
    setTimeout(() => {
      const messagesContainer = document.querySelector('[data-messages-container]');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }, 100);
    
    try {
      let response;
      switch (action) {
        case 'job-description':
          response = await geminiService.suggestJobDescription('Software Engineer', 'React, Node.js, 3+ years');
          break;
        case 'interview-questions':
          response = await geminiService.suggestInterviewQuestions('Full Stack Developer', 'Mid-level');
          break;
        case 'resume-tips':
          response = await geminiService.generateResponse('Give me tips for reviewing resumes effectively');
          break;
        case 'candidate-screening':
          response = await geminiService.generateResponse('What are the best practices for screening candidates in an ATS?');
          break;
        case 'email-templates':
          response = await geminiService.generateResponse('Help me create professional email templates for candidate communication');
          break;
        case 'assessment-tips':
          response = await geminiService.generateResponse('What criteria should I use to evaluate candidates effectively?');
          break;
        default:
          return;
      }

      const aiMessage = {
        id: Date.now(),
        text: response,
        type: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Quick action error:', error);
      // Provide fallback response for quick actions
      const fallbackResponse = geminiService.getFallbackResponse(action);
      const fallbackMessage = {
        id: Date.now(),
        text: fallbackResponse,
        type: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="AI Assistant"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          background: serviceStatus === 'busy' 
            ? 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)'
            : serviceStatus === 'offline'
            ? 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)'
            : 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
          '&:hover': {
            background: serviceStatus === 'busy' 
              ? 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)'
              : serviceStatus === 'offline'
              ? 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)'
              : 'linear-gradient(135deg, #0d47a1 0%, #1a237e 100%)',
            transform: 'scale(1.1)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: serviceStatus === 'busy' ? '#ff9800' : 
                              serviceStatus === 'offline' ? '#f44336' : '#1a237e'
            }
          }}
        >
          <AIIcon />
        </Badge>
      </Fab>

      {/* Chat Dialog with Quick Actions */}
      <ChatDialog
        isOpen={isOpen}
        onClose={handleClose}
        messages={messages}
        inputValue={inputValue}
        onInputChange={(e) => setInputValue(e.target.value)}
        onSendMessage={sendMessage}
        onKeyPress={handleKeyPress}
        isLoading={isLoading}
        serviceStatus={serviceStatus}
        loadingTime={loadingTime}
      >
        <QuickActions 
          onQuickAction={handleQuickAction}
          isLoading={isLoading}
        />
      </ChatDialog>
    </>
  );
};

export default AIAssistant;
